import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { Controlled as CodeMirror } from 'react-codemirror2';
import 'codemirror/lib/codemirror.css';
import 'codemirror/theme/material.css';
import 'codemirror/mode/javascript/javascript';
import { useAuth } from '../AuthContext';

const UpdatePrompts = () => {
  const { csrfToken, fetchCsrfToken } = useAuth();
  const [prompts, setPrompts] = useState([]);
  const [formData, setFormData] = useState({
    id: null,
    prompt_type: '',
    provider: 'openai',
    business_type: 'estate_sales',
    business_subtype: '', // Admin controls; required for new, enforced on save
    version: '',
    active: false,
    description: ''
  });
  const [promptText, setPromptText] = useState('');
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null); // Separate state for persistent warnings (e.g., missing standards)
  const [status, setStatus] = useState(''); // For success/error feedback
  const [loading, setLoading] = useState(true);
  const gridRef = useRef();
  const versionInputRef = useRef(null);
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePrompt, setDeletePrompt] = useState(null); // Selected prompt for modal
  const [originalData, setOriginalData] = useState(null); // Track original for key edits check

  useEffect(() => {
    async function ensureCsrfAndFetch() {
      let currentCsrf = csrfToken;
      if (!currentCsrf) {
        currentCsrf = await fetchCsrfToken();
      }
      if (currentCsrf) {
        fetchPrompts(currentCsrf);
      } else {
        setError('Failed to fetch CSRF token');
        setLoading(false);
      }
    }
    async function fetchPrompts(csrf) {
      setLoading(true);
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/supabase/rpc/select_all_prompts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-csrf-token': csrf
          },
          body: JSON.stringify({}),
          credentials: 'include',
        });
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        const data = await res.json();
        setPrompts(data || []);
      } catch (err) {
        setError('Failed to fetch prompts: ' + err.message);
      }
      setLoading(false);
    }
    ensureCsrfAndFetch();
  }, [csrfToken, fetchCsrfToken]);

  useEffect(() => {
    if (prompts.length > 0) {
      const hasStandardV1 = prompts.some(p => p.prompt_type === 'vision1' && (p.provider || 'openai') === 'openai' && p.business_type === 'estate_sales' && p.business_subtype === 'standard' && p.active);
      const hasStandardV2 = prompts.some(p => p.prompt_type === 'vision2' && (p.provider || 'openai') === 'openai' && p.business_type === 'estate_sales' && p.business_subtype === 'standard' && p.active);
      if (!hasStandardV1 || !hasStandardV2) {
        setWarning('Warning: Missing active OpenAI "standard" prompts for vision1 and/or vision2 under estate_sales. Please add them to ensure proper fallbacks.');
      } else {
        setWarning(null);
      }
    }
  }, [prompts]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const newData = { ...prev, [name]: type === 'checkbox' ? checked : value };
      console.log('Form state updated:', newData);
      // FIXED: Prevent key field changes on existing prompts
      if (newData.id && originalData && ['prompt_type', 'provider', 'business_type', 'business_subtype'].includes(name)) {
        if (newData[name] !== originalData[name]) {
          setError(`Key field "${name}" cannot be changed on existing prompt. Deactivate and create new.`);
          return prev; // Revert change
        }
      }
      return newData;
    });
  };

  const handleSave = async () => {
    if (!formData.prompt_type) {
      setError('Prompt type is required');
      return;
    }
    if (!formData.version) {
      setError('Version is required');
      return;
    }
    if (!promptText.trim()) {
      setError('Prompt text is required and cannot be empty');
      return;
    }
    if (!formData.id && (!formData.business_subtype || !formData.business_subtype.trim())) {
      setError('Business subtype is required for new prompts.');
      return;
    }
    // FIXED: For existing prompts, require non-key field changes
    if (formData.id && originalData) {
      const nonKeyFields = ['version', 'prompt_text', 'active', 'description'];
      const hasChanges = nonKeyFields.some(field => {
        if (field === 'prompt_text') return promptText.trim() !== (originalData.prompt_text || '').trim();
        return formData[field] !== originalData[field];
      });
      if (!hasChanges) {
        setError('No changes detected. Edit version, prompt text, active, or description.');
        return;
      }
    }
    // FIXED: Enforcement for active prompts (no multiples per subtype; require one for standard)
    const isStandard = formData.business_subtype === 'standard';
    if (formData.active) {
      const existingActives = prompts.filter(p => p.id !== formData.id && p.active && p.prompt_type === formData.prompt_type && (p.provider || 'openai') === formData.provider && p.business_type === formData.business_type && p.business_subtype === formData.business_subtype);
      if (existingActives.length > 0) {
        setError(`Multiple active prompts not allowed for this provider/subtype. Deactivate ${existingActives.length > 1 ? 'one' : 'the existing one'} first.`);
        return;
      }
    }
    if (isStandard && formData.active) {
      // Check if standard already exists (ensure exactly one)
      const existingStandard = prompts.filter(p => p.prompt_type === formData.prompt_type && (p.provider || 'openai') === formData.provider && p.business_type === 'estate_sales' && p.business_subtype === 'standard' && p.active && p.id !== formData.id);
      if (existingStandard.length > 0) {
        setError('Only one active standard prompt allowed per provider. Deactivate the existing one first.');
        return;
      }
    }
    setError(null);
    let currentCsrf = csrfToken;
    if (!currentCsrf) {
      currentCsrf = await fetchCsrfToken();
    }
    if (!currentCsrf) {
      setError('Failed to fetch CSRF token');
      return;
    }
    const rpcName = formData.id ? 'update_prompt' : 'insert_prompt';
    let params = {};
    if (rpcName === 'insert_prompt') {
      params = {
        p_prompt_type: formData.prompt_type,
        p_provider: formData.provider,
        p_business_type: formData.business_type,
        p_business_subtype: formData.business_subtype, // Enforced non-empty on button press
        p_version: formData.version,
        p_prompt_text: promptText,
        p_active: formData.active,
        p_description: formData.description
      };
    } else {
      params = {
        p_id: formData.id,
        p_version: formData.version,
        p_prompt_text: promptText,
        p_active: formData.active,
        p_description: formData.description
      };
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/supabase/rpc/${rpcName}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': currentCsrf
        },
        body: JSON.stringify(params),
        credentials: 'include',
      });
      if (!res.ok) {
        let errorText = await res.text();
        try {
          const errorJson = JSON.parse(errorText);
          errorText = errorJson.message || errorJson.error || errorText; // Extract SQL RAISE message if available
        } catch {
          // Fallback to plain text
        }
        setError(`Save failed: ${errorText}`); // FIXED: Specific messaging (e.g., "Subtype cannot be changed on existing prompt")
        return;
      }
      setStatus('Prompt saved successfully!'); // FIXED: Success feedback
      setTimeout(() => setStatus(''), 3000);
      // Invalidate cache (always on save, to be safe)
      const invalidateRes = await fetch(`${process.env.REACT_APP_API_URL}/supabase/invalidate_prompt_cache`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': currentCsrf
        },
        body: JSON.stringify({
          prompt_type: formData.prompt_type,
          provider: formData.provider,
          business_type: formData.business_type,
          business_subtype: formData.business_subtype || null
        }),
        credentials: 'include',
      });
      if (invalidateRes.ok) {
        const invData = await invalidateRes.json();
        console.log('Invalidation success:', invData.invalidated);
      } else {
        console.warn('Invalidation failed:', invalidateRes.status);
      }
      // Refresh list
      const refreshRes = await fetch(`${process.env.REACT_APP_API_URL}/supabase/rpc/select_all_prompts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': currentCsrf
        },
        body: JSON.stringify({}),
        credentials: 'include',
      });
      if (!refreshRes.ok) {
        throw new Error(`HTTP error! status: ${refreshRes.status}`);
      }
      const data = await refreshRes.json();
      setPrompts(data || []);
      handleReset();
    } catch (err) {
      setError(err.message); // FIXED: Specific RPC exceptions in UI
    }
  };

  const handleReset = () => {
    const newFormData = {
      id: null,
      prompt_type: '',
      provider: 'openai',
      business_type: 'estate_sales',
      business_subtype: '',
      version: '',
      active: false,
      description: ''
    };
    setFormData(newFormData);
    setPromptText('');
    console.log('Form reset to:', newFormData);
    if (versionInputRef.current) {
      versionInputRef.current.focus();
    }
  };

  const handleDeleteSelected = async () => {
    if (!gridRef.current || !gridRef.current.api) {
      setError('Grid not initialized');
      return;
    }
    const selectedRow = gridRef.current.api.getSelectedRows()[0];
    if (!selectedRow) {
      setError('No prompt selected for deletion');
      return;
    }
    setDeletePrompt(selectedRow); // Set for modal
    setShowDeleteModal(true); // Show modal instead of confirm
  };

  const confirmDelete = async () => {
    if (!deletePrompt) return;
    setShowDeleteModal(false);
    let currentCsrf = csrfToken;
    if (!currentCsrf) {
      currentCsrf = await fetchCsrfToken();
    }
    if (!currentCsrf) {
      setError('Failed to fetch CSRF token');
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/supabase/rpc/delete_prompt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': currentCsrf
        },
        body: JSON.stringify({ p_id: deletePrompt.id }),
        credentials: 'include',
      });
      if (!res.ok) {
        let errorText = await res.text();
        try {
          const errorJson = JSON.parse(errorText);
          errorText = errorJson.message || errorJson.error || errorText;
        } catch {
          // Fallback
        }
        throw new Error(`Delete failed: ${errorText}`);
      }
      setStatus('Prompt deleted successfully!');
      setTimeout(() => setStatus(''), 3000);
      // Invalidate cache
      const invalidateRes = await fetch(`${process.env.REACT_APP_API_URL}/supabase/invalidate_prompt_cache`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': currentCsrf
        },
        body: JSON.stringify({
          prompt_type: deletePrompt.prompt_type,
          provider: deletePrompt.provider || 'openai',
          business_type: deletePrompt.business_type,
          business_subtype: deletePrompt.business_subtype || null
        }),
        credentials: 'include',
      });
      if (invalidateRes.ok) {
        const invData = await invalidateRes.json();
        console.log('Invalidation success:', invData.invalidated);
      }
      // Refresh grid
      const refreshRes = await fetch(`${process.env.REACT_APP_API_URL}/supabase/rpc/select_all_prompts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': currentCsrf
        },
        body: JSON.stringify({}),
        credentials: 'include',
      });
      const refreshedData = await refreshRes.json();
      setPrompts(refreshedData || []);
      gridRef.current.api.deselectAll();
      handleReset();
    } catch (err) {
      setError(err.message);
    }
  };

  const onCellValueChanged = async (params) => {
    const { data, newValue, oldValue, colDef } = params;
    if (newValue === oldValue) return;
    let currentCsrf = csrfToken;
    if (!currentCsrf) {
      currentCsrf = await fetchCsrfToken();
    }
    if (!currentCsrf) return setError('CSRF fail');
    // FIXED: Specific validation for subtype change (not allowed on existing)
    if (['provider', 'business_subtype'].includes(colDef.field) && data.id) {
      setError('Provider and subtype cannot be changed on existing prompt. Deactivate and create new.');
      params.node.setDataValue(colDef.field, oldValue); // Revert
      return;
    }
    // For prompt_text field, add non-empty validation (inline edit doesn't use form, so check here)
    if (colDef.field === 'prompt_text' && !newValue.trim()) {
      setError('Prompt text cannot be empty');
      params.node.setDataValue(colDef.field, oldValue); // Revert
      return;
    }
    // FIXED: Enforcement for active toggle (no multiples per subtype)
    if (colDef.field === 'active' && newValue) {
      const existingActives = prompts.filter(p => p.id !== data.id && p.active && p.prompt_type === data.prompt_type && (p.provider || 'openai') === (data.provider || 'openai') && p.business_type === data.business_type && p.business_subtype === data.business_subtype);
      if (existingActives.length > 0) {
        setError(`Multiple active prompts not allowed for this provider/subtype. Deactivate ${existingActives.length > 1 ? 'one' : 'the existing one'} first.`);
        params.node.setDataValue(colDef.field, oldValue); // Revert
        return;
      }
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/supabase/rpc/update_prompt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': currentCsrf
        },
        body: JSON.stringify({
          p_id: data.id,
          p_version: colDef.field === 'version' ? newValue : data.version,
          p_prompt_text: colDef.field === 'prompt_text' ? newValue : data.prompt_text,
          p_active: colDef.field === 'active' ? newValue : data.active,
          p_description: colDef.field === 'description' ? newValue : data.description
        }),
        credentials: 'include',
      });
      if (!res.ok) {
        let errorText = await res.text();
        try {
          const errorJson = JSON.parse(errorText);
          errorText = errorJson.message || errorJson.error || errorText; // Extract SQL RAISE message
        } catch {
          // Fallback
        }
        setError(`Inline update failed: ${errorText}`); // FIXED: Specific messaging
        params.node.setDataValue(colDef.field, oldValue);
        return;
      }
      setStatus('Inline update successful!'); // FIXED: Success feedback
      setTimeout(() => setStatus(''), 3000);
      // Invalidate cache (added for inline edits; use updated row data)
      const invalidateRes = await fetch(`${process.env.REACT_APP_API_URL}/supabase/invalidate_prompt_cache`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': currentCsrf
        },
        body: JSON.stringify({
          prompt_type: data.prompt_type,
          provider: data.provider || 'openai',
          business_type: data.business_type,
          business_subtype: data.business_subtype || null
        }),
        credentials: 'include',
      });
      if (invalidateRes.ok) {
        const invData = await invalidateRes.json();
        console.log('Inline invalidation success:', invData.invalidated);
      } else {
        console.warn('Inline invalidation failed:', invalidateRes.status);
      }
      // Refresh grid
      const refreshRes = await fetch(`${process.env.REACT_APP_API_URL}/supabase/rpc/select_all_prompts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': currentCsrf
        },
        body: JSON.stringify({}),
        credentials: 'include',
      });
      const refreshedData = await refreshRes.json();
      setPrompts(refreshedData || []);
    } catch (err) {
      setError(err.message); // FIXED: Specific RPC exceptions in UI for inline edits too
      params.node.setDataValue(colDef.field, oldValue);
    }
  };

  const columnDefs = [
    { headerName: 'ID', field: 'id', flex: 1, editable: false },
    { headerName: 'Type', field: 'prompt_type', flex: 1, editable: false, sort: 'asc' }, // FIXED: Sort by type asc
    { headerName: 'Provider', field: 'provider', flex: 1, editable: false, sort: 'asc' },
    { headerName: 'Business Type', field: 'business_type', flex: 1, editable: false, sort: 'asc' }, // FIXED: Sort by business type asc
    { headerName: 'Subtype', field: 'business_subtype', flex: 1, editable: false, sort: 'asc' }, // FIXED: Sort by subtype asc
    { headerName: 'Version', field: 'version', flex: 1, editable: true },
    { headerName: 'Active', field: 'active', flex: 1, cellRenderer: (params) => (params.value ? 'Yes' : 'No'), editable: true, cellEditor: 'agSelectCellEditor', cellEditorParams: { values: [true, false] } },
    { headerName: 'Description', field: 'description', flex: 2, editable: true },
    { headerName: 'Prompt Text', field: 'prompt_text', flex: 3, editable: true }
  ];

  return (
    <div className="p-4 space-y-4">
      {error && <div className="text-red-500 p-2 bg-red-100 rounded">{error}</div>}
      {warning && <div className="text-red-500">{warning}</div>}
      {status && <div className="text-green-500 p-2 bg-green-100 rounded">{status}</div>} {/* FIXED: Success messaging */}
      {loading ? (
        <div className="text-center">Loading prompts...</div>
      ) : (
        <div className="ag-theme-alpine h-96 w-full">
          <AgGridReact
            ref={gridRef}
            rowData={prompts}
            columnDefs={columnDefs}
            defaultColDef={{ sortable: true }}
            defaultSortModel={[
              { colId: 'prompt_type', sort: 'asc' },
              { colId: 'provider', sort: 'asc' },
              { colId: 'business_type', sort: 'asc' },
              { colId: 'business_subtype', sort: 'asc' }
            ]}
            rowSelection="single" // FIXED: Enable single row selection on click
            editType="fullRow"
            onCellValueChanged={onCellValueChanged}
            onRowSelected={(event) => {
              if (event.node.isSelected()) {
                const selectedData = event.node.data;
                console.log('Row selected:', selectedData); // FIXED: Debug log to confirm trigger
                if (selectedData) {
                  setOriginalData(selectedData); // FIXED: Track original for key change prevention
                  setFormData({
                    id: selectedData.id,
                    prompt_type: selectedData.prompt_type,
                    provider: selectedData.provider || 'openai',
                    business_type: selectedData.business_type,
                    business_subtype: selectedData.business_subtype || '',
                    version: selectedData.version,
                    active: selectedData.active,
                    description: selectedData.description
                  });
                  setPromptText(selectedData.prompt_text || '');
                  console.log('Form populated:', { formData: selectedData, promptText: selectedData.prompt_text || '' }); // FIXED: Debug log
                }
              }
            }}
          />
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Prompt Type</label>
          <select
            name="prompt_type"
            value={formData.prompt_type}
            onChange={handleInputChange}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="">Select</option>
            <option value="vision1">Vision1</option>
            <option value="vision2">Vision2</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Provider</label>
          <select
            name="provider"
            value={formData.provider}
            onChange={handleInputChange}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="openai">OpenAI</option>
            <option value="anthropic">Anthropic</option>
            <option value="gemini">Gemini</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Business Type</label>
          <select
            name="business_type"
            value={formData.business_type}
            onChange={handleInputChange}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="">Select</option>
            <option value="estate_sales">Estate Sales</option>
            <option value="retail">Retail</option>
            <option value="ebay_sales">eBay Sales</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Business Subtype (required for new)</label>
          <input
            type="text"
            name="business_subtype"
            value={formData.business_subtype}
            onChange={handleInputChange}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Version</label>
          <input
            ref={versionInputRef}
            required
            type="text"
            name="version"
            value={formData.version}
            onChange={handleInputChange}
            className={`mt-1 block w-full rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 border ${!formData.version && error ? 'border-red-500' : 'border-gray-300'}`}
          />
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            name="active"
            checked={formData.active}
            onChange={handleInputChange}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label className="ml-2 block text-sm font-medium text-gray-700">Active</label>
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700">Description</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
            rows="3"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700">Prompt Text</label>
        <CodeMirror
          value={promptText}
          options={{
            mode: 'javascript',
            theme: 'material',
            lineNumbers: true,
            lineWrapping: true
          }}
          onBeforeChange={(editor, data, value) => setPromptText(value)}
        />
      </div>
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-sm">
            <h3 className="text-lg font-bold mb-4">Delete Prompt?</h3>
            <p className="mb-4">Delete "{deletePrompt?.version}" for {deletePrompt?.business_subtype}?</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => { setShowDeleteModal(false); setDeletePrompt(null); }} className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400">
                Cancel
              </button>
              <button onClick={confirmDelete} className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="flex space-x-4">
        <button
          onClick={handleSave}
          className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Save Prompt
        </button>
        <button
          onClick={handleReset}
          className="bg-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
        >
          Reset Form
        </button>
        <button
          onClick={() => navigate('/admin')}
          className="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
        >
          Back to Admin
        </button>
        <button
          onClick={() => navigate('/estate-sales-pipeline/uploader')}
          className="bg-green-500 text-white py-2 px-4 rounded hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          Open Uploader
        </button>
        <button
          onClick={handleDeleteSelected}
          className="bg-red-500 text-white py-2 px-4 rounded hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500"
          disabled={!gridRef.current?.api?.getSelectedRows()?.length}
        >
          Delete Selected
        </button>
      </div>
    </div>
  );
};

export default UpdatePrompts;
